import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Alert, Card, PageHeader, Spinner, useToast } from '@platform/ui';

import { SettingsForm } from '../../features/settings/settings-form';
import type { PlatformSettingsPatch } from '../../lib/api/platform';
import { usePlatformClient } from '../../lib/api/platform-client-context';
import { RequirePermission } from '../../lib/session/guards';
import { PLATFORM_PERMISSIONS, usePermissions } from '../../lib/session/permissions';
import { useStepUp } from '../../lib/session/step-up';

export const Route = createFileRoute('/settings/')({
  component: PlatformSettingsRoute,
});

/**
 * `/settings` (FR-SUPER-07, MASTER_PLAN M12.6, `Docs/API.md` §2.13).
 *
 * `PATCH /platform/settings` is a step-up endpoint, so every save goes through `guard()`
 * and is retried with the same patch once a second factor verifies. One request carries
 * the whole form: an operator edits a *page*, not a key/value store, and five writes that
 * can half-succeed is not a save.
 */
function PlatformSettingsRoute() {
  return (
    <RequirePermission keys={[PLATFORM_PERMISSIONS.settingsManage]}>
      <PlatformSettingsPage />
    </RequirePermission>
  );
}

function PlatformSettingsPage() {
  const client = usePlatformClient();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const { guard } = useStepUp();
  const { toast } = useToast();

  const [error, setError] = useState<string | undefined>(undefined);

  const settings = useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => client.getSettings(),
  });
  /* The default-plan select needs names, not keys: `defaultPlanKey` is what the API
     stores and "Clinic" is what an operator recognises. */
  const plans = useQuery({ queryKey: ['platform-plans'], queryFn: () => client.listPlans() });

  const save = useMutation({
    mutationFn: (patch: PlatformSettingsPatch) =>
      guard(() => client.saveSettings(patch), 'change platform settings'),
    onSuccess: async (next, patch) => {
      setError(undefined);
      toast({
        tone: 'success',
        message:
          patch.maintenanceMode === undefined
            ? 'Platform settings saved.'
            : next.maintenanceMode.enabled
              ? 'Maintenance mode is on. Patient booking is unavailable on every tenant.'
              : 'Maintenance mode is off. Patient booking is live again.',
      });
      await queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
    },
    onError: (caught: Error) => {
      setError(caught.message);
    },
  });

  if (settings.isPending) {
    return (
      <>
        <PageHeader title="Platform settings" />
        <Card className="p-6">
          <Spinner label="Loading platform settings" />
        </Card>
      </>
    );
  }

  if (settings.isError) {
    return (
      <>
        <PageHeader title="Platform settings" />
        <Alert tone="error">Platform settings could not be loaded. Try again in a moment.</Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Platform settings"
        description="What the platform is, and what every new tenant starts with. Each save is audited with your name."
      />
      <SettingsForm
        /* Remount when the saved values change, so the form's draft never drifts from
           what the server holds after a successful save. */
        key={JSON.stringify(settings.data)}
        settings={settings.data}
        saving={save.isPending}
        error={error}
        canManage={can(PLATFORM_PERMISSIONS.settingsManage)}
        plans={(plans.data ?? []).map((plan) => ({ key: plan.key, name: plan.name }))}
        plansLink={
          <Link to="/plans" className="text-primary-accessible hover:underline">
            Open plans
          </Link>
        }
        onSave={(patch) => {
          save.mutate(patch);
        }}
      />
    </>
  );
}
